import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  searchExternalMedia,
  importExternalMedia,
} from "../../services/mediaLibraryService";
import MediaPreview from "./MediaPreview";
import MediaLibraryPageSizeSelect from "./MediaLibraryPageSizeSelect";
import MediaLibraryPaginationBar from "./MediaLibraryPaginationBar";
import { DEFAULT_MEDIA_PAGE_SIZE } from "./mediaLibraryPagination";
import { buildSearchSuggestions } from "./mediaSearchSuggestions";
import { buildCropFileName, extractApiMessage, resolveCropImageUrl } from "./externalImportCropUtils";

const nullSafeText = (value) => (value == null ? "" : String(value));

const extractResults = (res) => {
  const data = res?.data;
  if (Array.isArray(data)) return data;
  const result = data?.result ?? data?.Result ?? data?.data ?? data?.Data;
  if (Array.isArray(result)) return result;
  return (
    result?.items ??
    result?.Items ??
    result?.results ??
    result?.photos ??
    result?.hits ??
    data?.items ??
    data?.results ??
    []
  );
};

const extractTotal = (res, fallback) => {
  const data = res?.data;
  const result = data?.result ?? data?.Result ?? data?.data ?? data?.Data ?? {};
  const total =
    result.totalCount ??
    result.total ??
    result.totalResults ??
    result.total_results ??
    data?.totalCount ??
    data?.total ??
    data?.totalResults;
  const num = Number(total);
  return Number.isFinite(num) && num > 0 ? num : fallback;
};

/** Highest-resolution URL per provider (matches each provider's response shape). */
const resolveProviderImageUrl = (raw, provider) => {
  const fromApi = raw.mediaUrl ?? raw.url ?? "";
  if (fromApi) return fromApi;

  const key = String(provider || "").toLowerCase();
  if (key === "pexels") {
    return raw.src?.original ?? "";
  }
  if (key === "pixabay") {
    return raw.largeImageURL ?? raw.fullHDURL ?? raw.imageURL ?? "";
  }
  if (key === "unsplash") {
    return raw.urls?.full ?? raw.urls?.regular ?? "";
  }
  return (
    raw.src?.original ??
    raw.largeImageURL ??
    raw.urls?.full ??
    raw.urls?.regular ??
    ""
  );
};

const resolveProviderThumbnailUrl = (raw, provider) => {
  const fromApi =
    raw.thumbnailUrl ??
    raw.thumbUrl ??
    raw.previewUrl ??
    raw.previewURL ??
    "";
  if (fromApi) return fromApi;

  const key = String(provider || "").toLowerCase();
  if (key === "pexels") {
    return raw.src?.medium ?? raw.src?.small ?? raw.src?.tiny ?? "";
  }
  if (key === "pixabay") {
    return raw.previewURL ?? raw.webformatURL ?? "";
  }
  if (key === "unsplash") {
    return raw.urls?.small ?? raw.urls?.thumb ?? "";
  }
  return (
    raw.thumbnail ??
    raw.src?.medium ??
    raw.src?.small ??
    raw.urls?.small ??
    raw.urls?.thumb ??
    raw.webformatURL ??
    ""
  );
};

/** Maps a provider's raw search item into a stable shape for the card. */
const normalizeResult = (raw, provider) => {
  if (!raw) return null;

  const providerKey = String(provider || "").toLowerCase();
  const mediaUrl = resolveProviderImageUrl(raw, providerKey);
  const thumbnailUrl = resolveProviderThumbnailUrl(raw, providerKey) || mediaUrl;
  const previewUrl =
    raw.previewUrl ?? raw.previewURL ?? thumbnailUrl ?? mediaUrl ?? "";

  return {
    provider: providerKey,
    externalId: raw.id ?? raw.externalId ?? raw.photoId ?? null,
    imageUrl: mediaUrl,
    mediaUrl,
    previewUrl,
    thumbnailUrl,
    mediaName:
      nullSafeText(
        raw.mediaName ?? raw.title ?? raw.alt ?? raw.description ?? raw.tags
      ) || "Untitled",
    authorName: nullSafeText(
      raw.photographer ??
        raw.author ??
        raw.user?.name ??
        raw.user ??
        raw.username ??
        raw.user_name
    ),
    width: raw.width ?? raw.imageWidth ?? raw.src?.width ?? null,
    height: raw.height ?? raw.imageHeight ?? raw.src?.height ?? null,
    sourceUrl: raw.sourceUrl ?? raw.url ?? raw.pageURL ?? raw.links?.html ?? "",
    providerType: providerKey,
    // Backward-compatible alias used by card UI
    author: nullSafeText(
      raw.photographer ??
        raw.author ??
        raw.user?.name ??
        raw.user ??
        raw.username ??
        raw.user_name
    ),
  };
};

const normalizeImported = (res, fallback) => {
  const data = res?.data;
  const raw = data?.result ?? data?.Result ?? data?.data ?? data ?? {};
  return {
    mediaAssetId:
      raw.mediaAssetId ?? raw.id ?? raw._id ?? raw.mediaId ?? raw.assetId,
    mediaUrl:
      raw.mediaUrl ?? raw.url ?? raw.fileUrl ?? fallback?.imageUrl ?? fallback?.mediaUrl ?? "",
    thumbnailUrl:
      raw.thumbnailUrl ??
      raw.thumbUrl ??
      raw.thumbnail ??
      raw.mediaUrl ??
      fallback?.thumbnailUrl ??
      "",
    mediaName:
      nullSafeText(raw.mediaName ?? raw.name ?? raw.title) ||
      fallback?.mediaName ||
      "Untitled",
    providerType:
      raw.providerType ?? raw.provider ?? fallback?.providerType ?? fallback?.provider ?? "",
    previewUrl:
      raw.previewUrl ??
      raw.previewURL ??
      fallback?.previewUrl ??
      "",
    width: raw.width ?? fallback?.width ?? null,
    height: raw.height ?? fallback?.height ?? null,
  };
};

/**
 * External provider search (Pexels, Pixabay, Unsplash, Freepik).
 * Full flow: search → display results → import → auto select (parent closes).
 */
const ProviderSearchTab = ({
  provider,
  providerName = "Provider",
  defaultKeyword = "",
  onSelect,
  onImportForCrop,
}) => {
  const [keyword, setKeyword] = useState("");
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_MEDIA_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [importingId, setImportingId] = useState(null);

  useEffect(() => {
    setKeyword(String(defaultKeyword || "").trim());
  }, [defaultKeyword]);

  const suggestions = useMemo(
    () => buildSearchSuggestions(defaultKeyword),
    [defaultKeyword]
  );

  const runSearch = useCallback(
    async (term, targetPage, size = pageSize) => {
      const trimmed = (term ?? "").trim();
      if (!trimmed) return;
      setLoading(true);
      setSearched(true);
      try {
        const res = await searchExternalMedia({
          provider,
          keyword: trimmed,
          page: targetPage,
          pageSize: size,
        });
        const list = extractResults(res)
          .map((item) => normalizeResult(item, provider))
          .filter(Boolean);
        setResults(list);
        setTotal(extractTotal(res, (targetPage - 1) * size + list.length));
        setPage(targetPage);
      } catch (err) {
        setResults([]);
        setTotal(0);
        const apiMessage = extractApiMessage(err);
        const isPixabayKeyError =
          String(provider).toLowerCase() === "pixabay" &&
          (apiMessage.toLowerCase().includes("invalid api key") ||
            err?.response?.status === 400);
        toast.error(
          isPixabayKeyError
            ? "Pixabay provider is currently unavailable."
            : apiMessage || `Search failed for ${providerName}.`
        );
      } finally {
        setLoading(false);
      }
    },
    [provider, providerName, pageSize]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    runSearch(keyword, 1);
  };

  const handlePageChange = (next) => {
    if (next < 1) return;
    runSearch(keyword, next, pageSize);
  };

  const handlePageSizeChange = (nextSize) => {
    setPageSize(nextSize);
    setPage(1);
    if (searched && keyword.trim()) {
      runSearch(keyword, 1, nextSize);
    }
  };

  const handlePreview = (item) => {
    if (item.mediaUrl) {
      window.open(item.mediaUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleImport = async (item) => {
    const externalImageUrl = item.imageUrl ?? item.mediaUrl ?? "";
    if (!externalImageUrl) {
      toast.error("Unable to import: image URL is missing.");
      return;
    }

    setImportingId(item.externalId ?? externalImageUrl);
    try {
      const payload = {
        ExternalImageUrl: externalImageUrl,
        Provider: provider,
        ExternalId:
          item.externalId != null ? String(item.externalId) : undefined,
        ThumbnailUrl: item.thumbnailUrl || undefined,
        MediaName: item.mediaName || undefined,
        Width: item.width ?? undefined,
        Height: item.height ?? undefined,
        AuthorName: item.authorName || item.author || undefined,
      };
      const res = await importExternalMedia(payload);
      const imported = normalizeImported(res, item);
      const cropSrc = resolveCropImageUrl(imported, item);
      if (!cropSrc) {
        toast.error("Unable to open cropper: image URL is missing.");
        return;
      }
      onImportForCrop?.({
        cropImage: cropSrc,
        cropFileName: buildCropFileName(imported.mediaName),
        pendingImport: imported,
      });
    } catch (err) {
      toast.error(extractApiMessage(err) || "Import failed. Please try again.");
    } finally {
      setImportingId(null);
    }
  };

  const totalPages = total > 0 ? Math.ceil(total / pageSize) : 0;

  return (
    <div className="xc-ml-provider">
      <form className="xc-ml-toolbar" onSubmit={handleSubmit}>
        <div className="xc-ml-search" style={{ maxWidth: 420 }}>
          <i className="ri-search-line" aria-hidden="true" />
          <input
            type="text"
            placeholder={`Search ${providerName} (e.g. Solar Energy, Wind Turbine)`}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </div>
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={loading || !keyword.trim()}
        >
          <i className="ri-search-line me-1" aria-hidden="true" />
          Search
        </button>
        {searched && !loading ? (
          <span className="xc-ml-toolbar__count">
            {total > 0 ? `${total} result(s)` : `${results.length} result(s)`}
          </span>
        ) : null}
        <span className="xc-ml-toolbar__spacer" />
        <MediaLibraryPageSizeSelect
          value={pageSize}
          onChange={handlePageSizeChange}
        />
      </form>

      {suggestions.length > 0 ? (
        <div className="xc-ml-suggestions">
          <span className="xc-ml-suggestions__label">Suggested:</span>
          <div className="xc-ml-suggestions__chips">
            {suggestions.map((chip) => (
              <button
                key={chip}
                type="button"
                className="xc-ml-suggestions__chip"
                onClick={() => setKeyword(chip)}
              >
                {chip}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="xc-ml-grid">
          {Array.from({ length: pageSize }).map((_, i) => (
            <div key={i} className="xc-ml-card">
              <div className="xc-ml-card__thumb">
                <div className="xc-skeleton" style={{ width: "100%", height: "100%" }} />
              </div>
              <div className="xc-ml-card__body">
                <div className="xc-skeleton" style={{ width: "70%", height: 12 }} />
                <div className="xc-skeleton" style={{ width: "40%", height: 10 }} />
              </div>
            </div>
          ))}
        </div>
      ) : !searched ? (
        <div className="xc-ml-empty">
          <i className="ri-search-eye-line" aria-hidden="true" />
          <span className="xc-ml-empty__title">Search {providerName}</span>
          <span className="xc-ml-empty__text">
            Enter a keyword and press Search to find royalty-free images.
          </span>
        </div>
      ) : results.length === 0 ? (
        <div className="xc-ml-empty">
          <i className="ri-image-2-line" aria-hidden="true" />
          <span className="xc-ml-empty__title">No images found.</span>
          <span className="xc-ml-empty__text">
            Try a different keyword or another provider.
          </span>
        </div>
      ) : (
        <>
          <div className="xc-ml-grid">
            {results.map((item) => {
              const isImporting =
                importingId === (item.externalId ?? item.mediaUrl);
              return (
                <div
                  key={item.externalId ?? item.mediaUrl}
                  className="xc-ml-card"
                >
                  <div className="xc-ml-card__thumb">
                    <span className="xc-ml-card__badge">
                      <i className="ri-image-line" aria-hidden="true" />
                      {providerName}
                    </span>
                    <MediaPreview
                      src={item.thumbnailUrl || item.previewUrl || item.mediaUrl}
                      alt={item.mediaName}
                    />
                  </div>

                  <div className="xc-ml-card__body">
                    <p className="xc-ml-card__name" title={item.mediaName}>
                      {item.mediaName}
                    </p>

                    <div className="xc-ml-card__meta">
                      {item.width && item.height ? (
                        <span>
                          <i className="ri-aspect-ratio-line" aria-hidden="true" />
                          {item.width} × {item.height}px
                        </span>
                      ) : null}
                      {item.author ? (
                        <span title={item.author}>
                          <i className="ri-user-line" aria-hidden="true" />
                          {item.author}
                        </span>
                      ) : null}
                    </div>

                    <div className="xc-ml-card__btns">
                      <button
                        type="button"
                        className="xc-ml-card__btn xc-ml-card__btn--ghost"
                        onClick={() => handlePreview(item)}
                        disabled={isImporting}
                      >
                        <i className="ri-eye-line" aria-hidden="true" />
                        Preview
                      </button>
                      <button
                        type="button"
                        className="xc-ml-card__btn xc-ml-card__btn--primary"
                        onClick={() => handleImport(item)}
                        disabled={Boolean(importingId)}
                      >
                        {isImporting ? (
                          <>
                            <span className="xc-ml-btn-spinner" />
                            Importing
                          </>
                        ) : (
                          <>
                            <i className="ri-download-2-line" aria-hidden="true" />
                            Import &amp; Select
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <MediaLibraryPaginationBar
            showPageSize={false}
            currentPage={page}
            totalPages={totalPages}
            totalEntries={total > 0 ? total : results.length}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      )}
    </div>
  );
};

export default ProviderSearchTab;
